import type { ValidationFieldError } from 'src/types/api';

// ----------------------------------------------------------------------

/**
 * Static Spanish fallbacks for go-playground/validator rule names.
 * These are used when i18next is not available or when the translation key
 * has not been defined yet.
 */
const FALLBACK_MESSAGES: Record<string, (err: ValidationFieldError) => string> = {
  required: () => 'Este campo es requerido',
  email:    () => 'Ingrese un correo electrónico válido',
  min:      (e) => `Mínimo ${e.param} caracteres`,
  max:      (e) => `Máximo ${e.param} caracteres`,
  len:      (e) => `Debe tener exactamente ${e.param} caracteres`,
  oneof:    (e) => `Debe ser uno de: ${e.param?.split(' ').join(', ')}`,
  url:      () => 'Ingrese una URL válida',
  numeric:  () => 'Solo se permiten números',
  alpha:    () => 'Solo se permiten letras',
};

// ----------------------------------------------------------------------

/**
 * Maps a single backend `ValidationFieldError` to a human-readable Spanish
 * message.
 *
 * When `t` is provided the function first tries the i18next key
 * `errors:validation.rules.<rule>` (namespace "errors", the same file that
 * holds the existing `validation.*` keys). If i18next returns an empty string
 * (key not found) it falls back to the static `FALLBACK_MESSAGES` map.
 *
 * @param err - The field-level error object from the backend `details` array.
 * @param t   - Optional i18next `t` function. Pass it to enable live
 *              translation switching; omit it in non-React contexts.
 */
export function validationMessage(
  err: ValidationFieldError,
  t?: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (t) {
    // Namespace "errors" contains the existing validation keys (errors.json).
    // Rule key lives at: errors:validation.rules.<rule>
    const key = `errors:validation.rules.${err.rule}`;
    const translated = t(key, { param: err.param, defaultValue: '' });
    if (translated) return translated;
  }

  return FALLBACK_MESSAGES[err.rule]?.(err) ?? `Validación fallida: ${err.rule}`;
}

// ----------------------------------------------------------------------

/**
 * Converts the backend `details` array (from a 422 response) into a plain
 * `field → message` map that can be fed directly into React Hook Form's
 * `setError()`.
 *
 * @example
 * // Inside a form submit handler:
 * } catch (err: any) {
 *   if (err.code === 'validation_error' && err.details) {
 *     const fieldErrors = parseValidationErrors(err.details, t);
 *     Object.entries(fieldErrors).forEach(([field, message]) => {
 *       methods.setError(field as any, { message });
 *     });
 *   }
 * }
 *
 * @param details - The `details` array from `ApiValidationError`.
 * @param t       - Optional i18next `t` function (passed through to
 *                  `validationMessage`).
 */
export function parseValidationErrors(
  details: ValidationFieldError[],
  t?: (key: string, opts?: Record<string, unknown>) => string
): Record<string, string> {
  return Object.fromEntries(
    details.map((err) => [err.field, validationMessage(err, t)])
  );
}
